import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTenantResolveQuery } from "../services/api";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setTenant } from "../features/tenant/tenantSlice";
import { PUBLIC_BRAND_NAME, SUPER_ADMIN_SLUG } from "../services/apiConfig";
import { isAdmin } from "../features/auth/roles";

export { SUPER_ADMIN_SLUG };

/** `192.168.1.8`, `10.0.0.4` — an address, not a name with a subdomain. */
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Which coaching a hostname belongs to.
 *
 * Tenancy is subdomain-based (`demo-academy.example.com` -> `demo-academy`), so
 * this reads the first label. That is only meaningful for an actual domain
 * name, and three kinds of host are not:
 *
 *   - **IP addresses.** Opening the app on a phone at `http://192.168.1.8:3100`
 *     split to `192` and asked the server to resolve a tenant called "192",
 *     which 404'd on every page load and left the app with no branding.
 *   - **Single-label hosts** (`mypc`, `android-tablet`). A name with no dot
 *     cannot carry a subdomain, so the whole name is the host, not a tenant.
 *   - **Loopback**, already handled.
 *
 * All of them mean "no tenant in the URL", which is the platform slug.
 */
export const deriveTenantSlug = (hostname?: string | null) => {
  if (!hostname) {
    return SUPER_ADMIN_SLUG;
  }
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  const localHosts = ["localhost", "0.0.0.0", "127.0.0.1", "::1"];
  if (localHosts.includes(normalized) || normalized.endsWith(".localhost")) {
    return SUPER_ADMIN_SLUG;
  }
  // IPv6 is detected by its colons, which can never appear in a hostname label.
  if (IPV4.test(normalized) || normalized.includes(":")) {
    return SUPER_ADMIN_SLUG;
  }
  const labels = normalized.split(".");
  // A bare host name has no subdomain to read.
  if (labels.length < 2) {
    return SUPER_ADMIN_SLUG;
  }
  const [subdomain] = labels;
  if (!subdomain || subdomain === "www") {
    return SUPER_ADMIN_SLUG;
  }
  return subdomain;
};

/**
 * The brand name a given viewer may see.
 *
 * On a plain host the tenant resolves to the PLATFORM record, whose
 * `display_name` is "SuperAdmin" — an internal operator label. It was removed
 * from the sidebar but not from the tab title, so a student's browser tab still
 * read "SuperAdmin | ...". Logged out counts as non-admin, keeping the operator
 * label off the public pages too.
 *
 * Pure so it can be tested without a store, a router or a DOM.
 */
export const resolveBrandName = (
  slug: string | null,
  role: string | null,
  displayName?: string | null
): string | null => {
  const isPlatformTenant = slug === SUPER_ADMIN_SLUG;
  const isAdminRole = isAdmin(role);
  if (isPlatformTenant && !isAdminRole) {
    return PUBLIC_BRAND_NAME;
  }
  return displayName ?? null;
};

export const useTenantBootstrap = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const authTenantSlug = useAppSelector((state) => state.auth.profile?.tenantSlug ?? null);
  const role = useAppSelector((state) => state.auth.profile?.role ?? null);
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  // An authenticated user's own tenant (from their JWT, restored on every boot via
  // hydrateFromStorage) always wins over the hostname guess — otherwise a page reload
  // silently drops a coaching-tenant user back onto "platform" and every subsequent
  // API call gets rejected with a tenant-mismatch 403.
  const slug = authTenantSlug ?? deriveTenantSlug(hostname);
  const { data } = useTenantResolveQuery(slug);

  useEffect(() => {
    if (data) {
      dispatch(setTenant({ slug, branding: data }));
    }
  }, [data, dispatch, slug]);

  useEffect(() => {
    // The landing page writes its own keyword-rich title and og: tags for
    // crawlers. This hook lives ABOVE it in the tree, and React runs child
    // effects before parent ones, so setting a title here unconditionally
    // overwrote that SEO title on every load of "/" — the one page whose title
    // is worth anything to search.
    if (location.pathname === "/") {
      return;
    }
    const brand = resolveBrandName(slug, role, data?.display_name);
    // "Coaching Intelligence" here was the tail of the old product name and
    // survived the rename, so a platform page read "Green Agentick | Coaching
    // Intelligence". A coaching tenant still gets its own name first; the
    // platform itself is not suffixed with itself.
    document.title = brand && brand !== PUBLIC_BRAND_NAME ? `${brand} | ${PUBLIC_BRAND_NAME}` : PUBLIC_BRAND_NAME;
  }, [data, location.pathname, role, slug]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const current = `${location.pathname}${location.search}`;
    try {
      const existingRaw = sessionStorage.getItem("recentPaths");
      const existing: string[] = existingRaw ? JSON.parse(existingRaw) : [];
      if (existing[0] === current) {
        return;
      }
      const next = [current, ...existing.filter((entry) => entry !== current)].slice(0, 5);
      sessionStorage.setItem("recentPaths", JSON.stringify(next));
    } catch {
      // ignore malformed history
    }
  }, [location.pathname, location.search]);
};

export const useTenantSlug = (options?: { fallback?: string | null }) => {
  const slug = useAppSelector((state) => state.tenant.slug);
  const authTenantSlug = useAppSelector((state) => state.auth.profile?.tenantSlug ?? null);
  if (slug) {
    return slug;
  }
  if (authTenantSlug) {
    return authTenantSlug;
  }
  if (options && Object.prototype.hasOwnProperty.call(options, "fallback")) {
    return options.fallback ?? null;
  }
  const hostname = typeof window !== "undefined" ? window.location.hostname : null;
  return deriveTenantSlug(hostname);
};
